import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Gift, Loader2, Zap, Ear, Timer } from "lucide-react";
import { API, BACKEND } from "@/assets";

export const GAME_META = {
  piano: { label: "Piano Scales", color: "#FF3399", tiers: "2,000 sample kit · 5,000 20% code · 10,000 free lease" },
  quiz: { label: "Venom Drop", color: "#00F0FF", tiers: "300 sample kit · 600 20% code · 1,000 free lease" },
  spiral: { label: "Serpent Spiral", color: "#A855F7", tiers: "150 sample kit · 300 20% code · 500 free lease" },
  bass: { label: "Drop the Bass", color: "#FFB800", tiers: "500 sample kit · 1,000 20% code · 2,000 free lease" },
  memory: { label: "Beat Builder", color: "#00FF66", tiers: "600 sample kit · 1,200 20% code · 1,800 free lease" },
};

/* ---------- shared: leaderboard + score claim ---------- */
export function MiniBoard({ game, refreshKey }) {
  const [board, setBoard] = useState(null);
  useEffect(() => {
    fetch(`${API}/arcade/leaderboard?game=${game}`)
      .then((r) => r.json()).then((d) => setBoard(d.leaderboard || [])).catch(() => setBoard([]));
  }, [game, refreshKey]);
  return (
    <div className="bg-black/50 border border-[#26262e] rounded-xl p-4" data-testid={`miniboard-${game}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-300 mb-3">Top Serpents — {GAME_META[game].label}</p>
      {board === null ? (
        <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-fuchsia-400" /></div>
      ) : board.length === 0 ? (
        <p className="text-xs text-ice/40 py-4 text-center">No scores yet — be the first legend.</p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
          {board.map((s, i) => (
            <div key={s.s_id} className="flex items-center gap-2.5 text-xs" data-testid={`miniboard-${game}-${s.s_id}`}>
              <span className={`w-6 font-mono ${i === 0 ? "text-yellow-300" : i < 3 ? "text-fuchsia-300" : "text-ice/40"}`}>#{i + 1}</span>
              <span className="text-ice/85 truncate flex-1">{s.name}</span>
              {s.prize && <Gift size={11} className="text-yellow-300/70 shrink-0" />}
              <span className="font-mono text-white">{s.score.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-[10px] text-ice/35 font-mono uppercase tracking-[0.12em]">Prizes: {GAME_META[game].tiers}</p>
    </div>
  );
}

export function ScoreClaim({ game, score, detail, minForPrize, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [done, setDone] = useState(null);

  const submit = async () => {
    if (!name.trim()) { setNote("Add a player name first."); return; }
    setBusy(true); setNote("");
    try {
      const res = await fetch(`${API}/arcade/score`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), score, best_combo: detail || 0, game }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setDone(data);
      onDone?.(data);
    } catch { setNote("Couldn't save — try again."); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="mt-4 text-center" data-testid={`${game}-submitted`}>
        <p className="text-sm text-emerald-300 font-bold">Rank #{done.rank} on the {GAME_META[game].label} board</p>
        {done.prize && <p className="text-xs text-yellow-200 mt-1.5">Prize earned: {done.prize}{done.prize_code ? ` — code ${done.prize_code}` : ""}{done.prize_code ? "" : " (link in your email)"}</p>}
        {done.prize_code && <p className="text-[10px] text-ice/45 mt-1 font-mono">Screenshot your code — it works once at beat checkout.</p>}
      </div>
    );
  }
  return (
    <div className="mt-4 w-full max-w-xs mx-auto space-y-2.5" data-testid={`${game}-claim`}>
      {score >= minForPrize && <p className="text-xs text-yellow-200/90 font-mono uppercase tracking-[0.12em] text-center">Prize tier reached — claim it</p>}
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Player name *" className="w-full bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-2.5 text-sm text-ice focus:outline-none focus:border-fuchsia-400/70" data-testid={`${game}-name`} />
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (for prize delivery)" className="w-full bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-2.5 text-sm text-ice focus:outline-none focus:border-fuchsia-400/70" data-testid={`${game}-email`} />
      <button onClick={submit} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold text-xs px-5 py-3 rounded-full hover:shadow-[0_0_20px_rgba(255,184,0,0.5)] active:scale-95 disabled:opacity-60 transition-all" data-testid={`${game}-submit-score`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />} Submit Score{score >= minForPrize ? " & Claim" : ""}
      </button>
      {note && <p className="text-xs text-red-300 text-center" data-testid={`${game}-submit-note`}>{note}</p>}
    </div>
  );
}

const Panel = ({ title, blurb, color, phase, onStart, children, over, claim }) => (
  <div className="relative bg-black/60 border rounded-2xl overflow-hidden min-h-[380px] flex flex-col" style={{ borderColor: `${color}44` }}>
    {phase === "playing" ? children : (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-10">
        <Zap size={28} style={{ color }} className="mb-3" />
        <p className="font-display text-white font-bold text-lg">{title}</p>
        <p className="text-xs text-ice/55 mt-2 max-w-xs">{phase === "over" ? over : blurb}</p>
        {phase === "over" && claim}
        <button onClick={onStart} className="mt-5 inline-flex items-center gap-2 text-white font-bold text-sm px-8 py-3.5 rounded-full active:scale-95 transition-all" style={{ background: `linear-gradient(90deg, ${color}, ${color}bb)`, boxShadow: `0 0 24px ${color}55` }} data-testid={`${title.toLowerCase().replace(/\s+/g, "-")}-start`}>
          <Play size={16} /> {phase === "over" ? "Play Again" : "Start"}
        </button>
      </div>
    )}
  </div>
);

/* ---------- Game B: Venom Drop (sound ID quiz) ---------- */
export function VenomDropQuiz({ tracks }) {
  const playable = tracks.filter((t) => t.file);
  const [phase, setPhase] = useState("idle");
  const [round, setRound] = useState(0);
  const [q, setQ] = useState(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(5);
  const [boardKey, setBoardKey] = useState(0);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const startedAt = useRef(0);
  const ROUNDS = 5;

  const stopAudio = () => { audioRef.current?.pause(); audioRef.current = null; };
  useEffect(() => () => { stopAudio(); clearInterval(timerRef.current); }, []);

  const nextRound = useCallback((r) => {
    stopAudio();
    const pool = [...playable].sort(() => Math.random() - 0.5);
    const answer = pool[r % pool.length];
    const options = [answer, ...pool.filter((t) => t.id !== answer.id)].slice(0, 4).map((t) => t.title).sort(() => Math.random() - 0.5);
    setQ({ answer: answer.title, options, file: answer.file });
    const a = new Audio(`${BACKEND}${answer.file}`);
    const offset = Math.floor(Math.random() * 15);
    a.currentTime = offset;
    a.play().catch(() => {});
    setTimeout(() => { try { a.pause(); } catch { /* noop */ } }, 3000);
    audioRef.current = a;
    startedAt.current = performance.now();
    setTimeLeft(5);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { // timed out — no points, next round
          clearInterval(timerRef.current);
          setTimeout(() => advance(r, false), 400);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  }, [playable]);

  const advance = (r, wasRight) => {
    if (r + 1 >= ROUNDS) { stopAudio(); setPhase("over"); }
    else { setRound(r + 1); nextRound(r + 1); }
    if (wasRight) setBoardKey((k) => k); // noop keep
  };

  const answer = (title) => {
    if (!q) return;
    const right = title === q.answer;
    if (right) {
      const speedBonus = Math.round((performance.now() - startedAt.current) / 50);
      setScore((s) => s + Math.max(60, 200 - speedBonus));
    }
    clearInterval(timerRef.current);
    stopAudio();
    if (round + 1 >= ROUNDS) setPhase("over");
    else { setRound(round + 1); nextRound(round + 1); }
  };

  const start = () => { setScore(0); setRound(0); setPhase("playing"); nextRound(0); };

  if (playable.length < 2) {
    return (
      <Panel title="Venom Drop" color={GAME_META.quiz.color} phase="idle" onStart={() => {}}
        blurb="This trial needs beats in the catalog — check back once the label loads more Soundscapes." />
    );
  }

  return (
    <div className="flex flex-col gap-4">
    <Panel title="Venom Drop" color={GAME_META.quiz.color} phase={phase} onStart={start}
      blurb="Three seconds of a Cold-Blooded Soundscape. Five seconds to name it. Faster ears, bigger points — 5 rounds."
      over={`Final score: ${score}`}
      claim={<ScoreClaim game="quiz" score={score} detail={0} minForPrize={300} onDone={() => setBoardKey((k) => k + 1)} />}>
      <div className="flex-1 flex flex-col p-5" data-testid="quiz-arena">
        <div className="flex items-center justify-between mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice/50">Round {round + 1}/{ROUNDS}</span>
          <span className="font-mono text-sm text-white" data-testid="quiz-score">{score}</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-5">
          <div className="h-full bg-gradient-to-r from-teal-400 to-fuchsia-500 transition-all duration-1000 ease-linear" style={{ width: `${(timeLeft / 5) * 100}%` }} data-testid="quiz-timer" />
        </div>
        <div className="flex items-center justify-center gap-2 py-6">
          <Ear size={22} className="text-teal-300 animate-pulse" />
          <p className="text-sm text-ice/70 font-mono uppercase tracking-[0.15em]">Snip in the air…</p>
        </div>
        <div className="grid grid-cols-2 gap-2.5 mt-auto">
          {(q?.options || []).map((opt) => (
            <button key={opt} onClick={() => answer(opt)} className="border border-teal-500/35 text-ice/85 text-xs sm:text-sm font-semibold rounded-xl px-3 py-3.5 hover:bg-teal-500/15 hover:border-teal-300 active:scale-95 transition-all" data-testid={`quiz-opt-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {opt}
            </button>
          ))}
        </div>
      </div>
    </Panel>
    <MiniBoard game="quiz" refreshKey={boardKey} />
    </div>
  );
}

/* ---------- Game D: Drop the Bass (timing tap) ---------- */
export function DropTheBass() {
  const [phase, setPhase] = useState("idle");
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [boardKey, setBoardKey] = useState(0);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const stRef = useRef(null);
  const ROUNDS = 10;

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const drawFrame = useCallback((now) => {
    const canvas = canvasRef.current;
    const st = stRef.current;
    if (!st || st.over) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width = canvas.offsetWidth;
    const h = canvas.height = canvas.offsetHeight;
    ctx.clearRect(0, 0, w, h);
    // fake waveform
    ctx.fillStyle = "rgba(255,184,0,0.15)";
    for (let x = 0; x < w; x += 4) {
      const amp = Math.abs(Math.sin(x * 0.06)) * (h * 0.32) + Math.sin(x * 0.013) * 10;
      ctx.fillRect(x, h / 2 - amp / 2, 2, amp);
    }
    // drop zone
    const zx = st.zone * w;
    ctx.fillStyle = "rgba(255,184,0,0.28)";
    ctx.fillRect(zx - st.zoneW / 2, 0, st.zoneW, h);
    ctx.strokeStyle = "#FFB800"; ctx.lineWidth = 2;
    ctx.strokeRect(zx - st.zoneW / 2, 0, st.zoneW, h);
    // playhead
    const px = st.pos * w;
    ctx.shadowColor = "#00F0FF"; ctx.shadowBlur = 14;
    ctx.fillStyle = "#00F0FF";
    ctx.fillRect(px - 2, 0, 4, h);
    ctx.shadowBlur = 0;
    st.pos += st.speed;
    if (st.pos > 1.08) { // missed the drop entirely
      st.over = true;
      setFeedback("The drop passed you by…");
      setTimeout(() => setPhase("over"), 700);
      return;
    }
    rafRef.current = requestAnimationFrame(drawFrame);
  }, []);

  const launchRound = useCallback((r) => {
    stRef.current = { pos: -0.05, zone: 0.62 + Math.random() * 0.28, zoneW: Math.max(26, 90 - r * 6), speed: 0.004 + r * 0.0009, over: false };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(drawFrame);
  }, [drawFrame]);

  const start = () => {
    setScore(0); setRound(0); setFeedback("");
    setPhase("playing");
    launchRound(0);
  };

  const tap = useCallback(() => {
    const st = stRef.current;
    if (!st || st.over || phase !== "playing") return;
    st.over = true;
    cancelAnimationFrame(rafRef.current);
    const d = Math.abs(st.pos - st.zone);
    const tol = st.zoneW / 2 / (canvasRef.current?.offsetWidth || 600);
    let pts = 0;
    if (d <= tol * 0.35) { pts = 200; setFeedback("PERFECT IMPACT +200"); }
    else if (d <= tol) { pts = 60; setFeedback("Glancing blow +60"); }
    else setFeedback("Whiffed it +0");
    setScore((s) => s + pts);
    if (round + 1 >= ROUNDS) setTimeout(() => setPhase("over"), 700);
    else { setRound(round + 1); setTimeout(() => launchRound(round + 1), 700); }
  }, [phase, round, launchRound]);

  useEffect(() => {
    const onKey = (e) => { if (e.code === "Space") { e.preventDefault(); tap(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tap]);

  return (
    <div className="flex flex-col gap-4">
    <Panel title="Drop the Bass" color={GAME_META.bass.color} phase={phase} onStart={start}
      blurb="The playhead charges the 808. Slam SPACEBAR (or tap) exactly when it hits the gold drop zone — 10 drops, shrinking zones, rising speed."
      over={`Final score: ${score}`}
      claim={<ScoreClaim game="bass" score={score} detail={0} minForPrize={500} onDone={() => setBoardKey((k) => k + 1)} />}>
      <div className="flex-1 flex flex-col p-5" data-testid="bass-arena">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice/50">Drop {round + 1}/{ROUNDS}</span>
          <span className="font-mono text-sm text-white" data-testid="bass-score">{score}</span>
        </div>
        <canvas ref={canvasRef} className="w-full h-36 sm:h-44 rounded-xl bg-black/40 cursor-pointer" onPointerDown={tap} data-testid="bass-canvas" />
        <p className="mt-3 text-center text-xs font-mono text-yellow-300 h-4" data-testid="bass-feedback">{feedback}</p>
        <button onClick={tap} className="mt-auto mx-auto inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-sm px-10 py-3.5 rounded-full active:scale-90 transition-all" data-testid="bass-tap">
          <Timer size={15} /> DROP
        </button>
      </div>
    </Panel>
    <MiniBoard game="bass" refreshKey={boardKey} />
    </div>
  );
}

/* ---------- Game C: Serpent Spiral (BPM & pitch puzzle) ---------- */
export function SerpentSpiral({ engine }) {
  const [phase, setPhase] = useState("idle");
  const [round, setRound] = useState(0);
  const [target, setTarget] = useState(null);
  const [bpm, setBpm] = useState(90);
  const [pitch, setPitch] = useState(3);
  const [score, setScore] = useState(0);
  const [msg, setMsg] = useState("");
  const [boardKey, setBoardKey] = useState(0);
  const startTime = useRef(0);
  const ROUNDS = 3;

  const clickTrack = (useBpm, semitone) => {
    const ctx = engine?.current?.ensureCtx?.();
    if (!ctx) return;
    const o = ctx.createOscillator();
    o.type = "square";
    o.frequency.value = 440 * Math.pow(2, semitone / 12);
    const g = ctx.createGain();
    const t = ctx.currentTime;
    const interval = 60 / useBpm;
    for (let i = 0; i < 8; i++) {
      g.gain.setValueAtTime(0.12, t + i * interval);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * interval + 0.05);
    }
    o.connect(g); g.connect(ctx.destination);
    o.start(t); o.stop(t + 8 * interval);
  };

  const start = () => {
    setScore(0); setRound(0); setMsg("");
    const t = { bpm: 100 + Math.floor(Math.random() * 60), semitone: Math.floor(Math.random() * 7) - 3 };
    setTarget(t);
    setBpm(60 + Math.floor(Math.random() * 60));
    setPitch(Math.floor(Math.random() * 7) - 3);
    startTime.current = performance.now();
    setPhase("playing");
  };

  const lockIn = () => {
    const bpmOk = Math.abs(bpm - target.bpm) <= 2;
    const pitchOk = pitch === target.semitone;
    if (bpmOk && pitchOk) {
      const secs = (performance.now() - startTime.current) / 1000;
      const pts = Math.max(50, Math.round(200 - secs * 8));
      setScore((s) => s + pts);
      setMsg(`Aligned! +${pts}`);
      if (round + 1 >= ROUNDS) setTimeout(() => setPhase("over"), 800);
      else {
        setRound(round + 1);
        const t = { bpm: 100 + Math.floor(Math.random() * 60), semitone: Math.floor(Math.random() * 7) - 3 };
        setTarget(t);
        setBpm(60 + Math.floor(Math.random() * 60));
        setPitch(Math.floor(Math.random() * 7) - 3);
        startTime.current = performance.now();
      }
    } else {
      setMsg(`Off resonance — ${bpmOk ? "BPM locked" : `BPM drift ${Math.abs(bpm - target.bpm)}`} · ${pitchOk ? "pitch locked" : "pitch off"}. Keep tuning.`);
    }
  };

  return (
    <div className="flex flex-col gap-4">
    <Panel title="Serpent Spiral" color={GAME_META.spiral.color} phase={phase} onStart={start}
      blurb="The ritual scrambled tempo and pitch. Slide the crystal dials until your frequency matches the target — lock in fast for max points. 3 rounds."
      over={`Final score: ${score}`}
      claim={<ScoreClaim game="spiral" score={score} detail={0} minForPrize={150} onDone={() => setBoardKey((k) => k + 1)} />}>
      <div className="flex-1 flex flex-col p-5" data-testid="spiral-arena">
        <div className="flex items-center justify-between mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice/50">Round {round + 1}/{ROUNDS}</span>
          <span className="font-mono text-sm text-white" data-testid="spiral-score">{score}</span>
        </div>
        <div className="text-center mb-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-purple-300">Target resonance</p>
          <p className="font-display text-2xl font-bold text-white" data-testid="spiral-target">{target?.bpm} BPM · {target && (target.semitone >= 0 ? `+${target.semitone}` : target.semitone)} st</p>
          <button onClick={() => clickTrack(target.bpm, target.semitone)} className="mt-2 text-[10px] font-mono uppercase tracking-[0.15em] text-teal-300 border border-teal-500/40 rounded-full px-4 py-1.5 hover:bg-teal-500/15 transition-all" data-testid="spiral-hear-target">Hear Target</button>
        </div>
        <div className="space-y-5">
          <div>
            <div className="flex justify-between mb-1.5"><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ice/55">Your BPM</span><span className="font-mono text-xs text-white" data-testid="spiral-bpm">{bpm}</span></div>
            <input type="range" min="60" max="180" value={bpm} onChange={(e) => setBpm(Number(e.target.value))} className="w-full accent-purple-500" data-testid="spiral-bpm-slider" />
          </div>
          <div>
            <div className="flex justify-between mb-1.5"><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ice/55">Your Pitch</span><span className="font-mono text-xs text-white" data-testid="spiral-pitch">{pitch >= 0 ? `+${pitch}` : pitch} st</span></div>
            <input type="range" min="-5" max="5" value={pitch} onChange={(e) => setPitch(Number(e.target.value))} className="w-full accent-purple-500" data-testid="spiral-pitch-slider" />
          </div>
          <button onClick={() => clickTrack(bpm, pitch)} className="text-[10px] font-mono uppercase tracking-[0.15em] text-ice/55 border border-white/15 rounded-full px-4 py-1.5 hover:border-purple-400/60 hover:text-purple-300 transition-all" data-testid="spiral-hear-yours">Hear Yours</button>
        </div>
        {msg && <p className="mt-3 text-center text-xs text-purple-200" data-testid="spiral-msg">{msg}</p>}
        <button onClick={lockIn} className="mt-auto mx-auto inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white font-bold text-sm px-10 py-3.5 rounded-full active:scale-95 transition-all" data-testid="spiral-lock">
          Lock In Resonance
        </button>
      </div>
    </Panel>
    <MiniBoard game="spiral" refreshKey={boardKey} />
    </div>
  );
}

/* ---------- Game E: Beat Builder (memory grid) ---------- */
const PADS = [
  { id: 0, label: "Drums", color: "#FF3399", freq: 110 },
  { id: 1, label: "Bass", color: "#00F0FF", freq: 82 },
  { id: 2, label: "Melody", color: "#A855F7", freq: 330 },
  { id: 3, label: "Vocals", color: "#00FF66", freq: 220 },
];

export function BeatBuilder({ engine }) {
  const [phase, setPhase] = useState("idle");
  const [seq, setSeq] = useState([]);
  const [userStep, setUserStep] = useState(0);
  const [lit, setLit] = useState(null);
  const [score, setScore] = useState(0);
  const [msg, setMsg] = useState("");
  const [watching, setWatching] = useState(false);
  const [boardKey, setBoardKey] = useState(0);

  const tone = (pad, dur = 0.28) => {
    const ctx = engine?.current?.ensureCtx?.();
    if (!ctx) return;
    const o = ctx.createOscillator();
    o.type = pad.id === 1 ? "sine" : "triangle";
    o.frequency.value = pad.freq;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(0.25, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g); g.connect(ctx.destination);
    o.start(t); o.stop(t + dur + 0.02);
  };

  const playSeq = useCallback(async (sequence) => {
    setWatching(true);
    setMsg("Watch the pattern…");
    for (const p of sequence) {
      await new Promise((r) => setTimeout(r, 560));
      setLit(p);
      tone(PADS[p]);
      await new Promise((r) => setTimeout(r, 300));
      setLit(null);
    }
    setWatching(false);
    setMsg("Your turn — repeat the pattern.");
    setUserStep(0);
  }, []);

  const start = () => {
    const first = Array.from({ length: 3 }, () => Math.floor(Math.random() * 4));
    setSeq(first);
    setScore(0);
    setPhase("playing");
    playSeq(first);
  };

  const tapPad = (pad) => {
    if (watching || phase !== "playing") return;
    tone(PADS[pad]);
    setLit(pad);
    setTimeout(() => setLit(null), 220);
    if (pad === seq[userStep]) {
      const pts = 100 * (Math.floor(seq.length / 3) || 1);
      const newScore = score + pts;
      setScore(newScore);
      if (userStep + 1 >= seq.length) {
        const next = [...seq, Math.floor(Math.random() * 4)];
        setSeq(next);
        setMsg(`Pattern grows — ${next.length} hits.`);
        setTimeout(() => playSeq(next), 900);
      } else {
        setUserStep(userStep + 1);
      }
    } else {
      setMsg("The pattern broke.");
      setTimeout(() => setPhase("over"), 600);
    }
  };

  return (
    <div className="flex flex-col gap-4">
    <Panel title="Beat Builder" color={GAME_META.memory.color} phase={phase} onStart={start}
      blurb="Simon-says with stems. Watch the glowing pattern, then play it back pad-for-pad. Every round the sequence grows — one wrong pad ends the run."
      over={`Final score: ${score}`}
      claim={<ScoreClaim game="memory" score={score} detail={seq.length} minForPrize={600} onDone={() => setBoardKey((k) => k + 1)} />}>
      <div className="flex-1 flex flex-col p-5" data-testid="memory-arena">
        <div className="flex items-center justify-between mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice/50">Pattern length {seq.length}</span>
          <span className="font-mono text-sm text-white" data-testid="memory-score">{score}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 flex-1">
          {PADS.map((p) => (
            <button key={p.id} onClick={() => tapPad(p.id)} disabled={watching}
              className="rounded-2xl border text-center transition-all duration-150 active:scale-95 disabled:cursor-wait py-8 sm:py-10"
              style={{
                borderColor: lit === p.id ? p.color : "#26262c",
                background: lit === p.id ? `${p.color}22` : "rgba(255,255,255,0.03)",
                boxShadow: lit === p.id ? `0 0 26px ${p.color}66` : "none",
              }}
              data-testid={`memory-pad-${p.id}`}>
              <p className="font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: lit === p.id ? p.color : "#71717a" }}>{p.label}</p>
            </button>
          ))}
        </div>
        <p className="mt-3 text-center text-xs font-mono text-emerald-300 h-4" data-testid="memory-msg">{msg}</p>
      </div>
    </Panel>
    <MiniBoard game="memory" refreshKey={boardKey} />
    </div>
  );
}
